import { Checkmark } from '@/components/icons/Checkmark';
import { getStepContent } from '@/utils/getStepContent';
import { getStepName } from '@/utils/stepUtils';
import { Checkbox, Flex, Group, SimpleGrid, Stack } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/router';
import { useState } from 'react';
import { CheckAndDisplaySalesBanner } from '../components/CheckAndDisplaySalesBanner';
import { PrimaryButton } from '../components/PrimaryButton';
import { StepForm } from '../components/StepForm';
import { StepSubtitle } from '../components/StepSubtitle';
import { StepTitle } from '../components/StepTitle';
import { choiceAndOpenStepContent } from '../content/choiceAndOpenStepContent';
import { choiceStepRoute } from '../router/router';
import { FieldName, IQuestionChoice, Step, StepName } from '../state/state.types';
import { useMdBreakpoint } from '../theme/hooks/useMdBreakpoint';
import { useCaseUpdater } from './hooks/useCaseUpdater';
import { useMultiChoiceStepResponse } from './hooks/useMultiChoiceStepResponse';
import { ContentContainer } from '@/components/ContentContainer';

export const MultiChoiceStep = () => {
  const isMdBreakpoint = useMdBreakpoint();
  const { stepId } = useParams({ from: choiceStepRoute.id });
  const { data: step } = useQuery<Step>({
    queryKey: ['step', stepId],
    enabled: !!stepId,
  });

  const { onFormSubmit, isLoading } = useCaseUpdater({});

  const stepName = getStepName(stepId);

  const [answers, setAnswers] = useState<string[]>([]);

  const getFieldName = () => {
    if (stepId === StepName.concerns) return FieldName.concerns;
    return FieldName.pain_point;
  };

  useMultiChoiceStepResponse(getFieldName(), setAnswers, stepId);

  const handleOnChange = (newValues: string[]) => {
    if (newValues.length > 3) return;

    setAnswers(newValues);
  };

  const cols = () => {
    if (stepId === StepName.stress_level) return 5;
    if ([StepName.pain_point_select_001, StepName.concerns].includes(stepId))
      return isMdBreakpoint ? 2 : 1;
    return 1;
  };

  if (!stepId || !stepName) throw new Error('StepId and StepName should be defined');

  const { title, subtitle } = getStepContent(stepId, choiceAndOpenStepContent);

  const isCheckboxDisabled = (choiceId: IQuestionChoice['id']) =>
    answers.length >= 3 && !answers.find((el) => el === choiceId);

  return (
    // To avoid memoizing the component and keeping the state
    // when we navigate between the same type of steps
    // we use pathname as a key
    <Flex direction="column">
      {!!title && (typeof title === 'string' ? <StepTitle>{title}</StepTitle> : title())}
      {!!subtitle && <StepSubtitle>{subtitle}</StepSubtitle>}
      <ContentContainer>
        <StepForm onSubmit={onFormSubmit}>
          <input
            name="stepId"
            type="hidden"
            value={stepId}
          />
          <Checkbox.Group
            value={answers}
            onChange={handleOnChange}
          >
            <Stack spacing={12}>
              <SimpleGrid
                mt={24}
                cols={cols()}
                spacing="sm"
                verticalSpacing="sm"
              >
                {step?.choices?.map((choice) => (
                  <Checkbox
                    key={choice.id}
                    label={
                      <Group
                        position="apart"
                        noWrap
                      >
                        {choice.display}
                        {answers.find((el) => el === choice.id) && (
                          <Checkmark
                            width={20}
                            height={20}
                          />
                        )}
                      </Group>
                    }
                    disabled={isCheckboxDisabled(choice.id)}
                    name={choice.id}
                    value={choice.id}
                    icon={() => null}
                    styles={(theme) => ({
                      root: {
                        border: answers.find((el) => el === choice.id)
                          ? `1px solid ${theme.colors.primary[0]}`
                          : `1px solid ${theme.colors.primary[2]}`,
                        borderRadius: 4,
                        padding: `0 16px`,
                        // REVIEW: make it according to design system when reviewed in Figma
                        backgroundColor: answers.find((el) => el === choice.id)
                          ? theme.colors.primary[0]
                          : theme.colors.minor[1],
                      },

                      inner: {
                        width: 0,
                        height: 0,
                        display: 'none',
                      },

                      body: {
                        height: '100%',
                        width: '100%',
                      },

                      labelWrapper: {
                        width: '100%',
                        height: '100%',
                      },

                      label: {
                        // override Mantine style
                        whiteSpace: 'normal',
                        cursor: 'pointer',
                        fontSize: theme.fontSizes.md,
                        padding: `12px 0`,
                        color: answers.find((el) => el === choice.id)
                          ? theme.colors.secondary[0]
                          : theme.colors.primary[0],
                        userSelect: 'none',
                        lineHeight: theme.lineHeight,

                        '&[data-disabled]': {
                          color: theme.colors.neutral[2],
                        },
                      },
                    })}
                  />
                ))}
              </SimpleGrid>
            </Stack>
          </Checkbox.Group>
          <Flex
            pos={isMdBreakpoint ? 'initial' : 'fixed'}
            bottom={0}
            left={0}
            px={isMdBreakpoint ? 0 : '2rem'}
            pb={isMdBreakpoint ? 0 : 30}
            style={{
              zIndex: 2,
              background: 'white',
              boxShadow: isMdBreakpoint ? 'none' : `0px -20px 20px white`,
              width: isMdBreakpoint ? 'auto' : '100vw',
            }}
          >
            <PrimaryButton
              isLoading={isLoading}
              isDisabled={answers.length === 0 || answers.length > 3}
              align={isMdBreakpoint ? 'end' : 'center'}
              mr="auto"
            >
              Continue
            </PrimaryButton>
          </Flex>
        </StepForm>
        {CheckAndDisplaySalesBanner(stepId)}
      </ContentContainer>
    </Flex>
  );
};
